import { inject, Injectable } from '@angular/core';
import { bbox as turfBbox } from '@turf/turf';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { LayerSpecification, LngLatBoundsLike, StyleSpecification } from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';

import { AssertService } from '@connect-services/client/services/assert.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import {
    LayerStyle,
    LayerStylesService,
    MapboxStyle,
    NetworkPathDto,
    NetworkPathEdgeDto,
    NetworkPathNodeDto,
} from '@connect-services/src/sdk/api';

export const NETWORK_PATH_NODE_SOURCE_ID = 'network-path-nodes';
export const NETWORK_PATH_EDGE_SOURCE_ID = 'network-path-edges';
export const NETWORK_PATH_ALL_ROUTES_SOURCE_ID = 'network-path-all-routes';

type NetworkPathMapItem = NetworkPathNodeDto | NetworkPathEdgeDto;

@Injectable({
    providedIn: 'root',
})
export class NetworkPathMapService extends MapBaseService {
    private currentAllRoutesStyle: MapboxStyle[] = [];
    private currentEdgeStyle: MapboxStyle[] = [];
    private currentNodeStyle: MapboxStyle[] = [];
    private displayRequestId = 0;

    private readonly layerStylesService = inject(LayerStylesService);

    public async displayNetworkPath(networkPath: NetworkPathDto, displayAllRoutes: boolean): Promise<void> {
        const requestId = ++this.displayRequestId;

        if (!this.isMapPresent()) {
            return;
        }

        const layerStyles = networkPath.networkPathLayerStyles;

        if (!layerStyles) {
            this.resetNetworkPath();

            return;
        }

        if (displayAllRoutes) {
            await this.loadAndDisplayAllRoutes(networkPath, layerStyles.allRoutesLayerStyleId, requestId);

            return;
        }

        await this.loadAndDisplayRoute(
            networkPath,
            layerStyles.edgeLayerStyleId,
            layerStyles.nodeLayerStyleId,
            requestId,
        );
    }

    public moveNetworkPathToTop(): void {
        if (!this.isMapPresent()) {
            return;
        }

        if (this.map.getSource(NETWORK_PATH_EDGE_SOURCE_ID)) {
            this.addSourceStyle(NETWORK_PATH_EDGE_SOURCE_ID, this.currentEdgeStyle);
        }

        if (this.map.getSource(NETWORK_PATH_NODE_SOURCE_ID)) {
            this.addSourceStyle(NETWORK_PATH_NODE_SOURCE_ID, this.currentNodeStyle);
        }

        if (this.map.getSource(NETWORK_PATH_ALL_ROUTES_SOURCE_ID)) {
            this.addSourceStyle(NETWORK_PATH_ALL_ROUTES_SOURCE_ID, this.currentAllRoutesStyle);
        }
    }

    public resetNetworkPath(): void {
        this.displayRequestId++;

        this.currentEdgeStyle = [];
        this.currentNodeStyle = [];
        this.currentAllRoutesStyle = [];

        if (!this.isMapPresent()) {
            return;
        }

        this.resetSources();
    }

    private centerMapOnNetworkPath(networkPath: NetworkPathDto): void {
        if (!this.isMapPresent()) {
            return;
        }

        const featureCollection: FeatureCollection<Geometry, GeoJsonProperties> = this.toFeatureCollection([
            ...networkPath.edges,
            ...networkPath.nodes,
        ]);

        if (!featureCollection.features.length) {
            return;
        }

        const bbox = turfBbox(featureCollection) as LngLatBoundsLike;

        this.map.fitBounds(bbox, {
            duration: 500,
            maxZoom: 18,
            padding: 80,
        });
    }

    private displayAllRoutes(networkPath: NetworkPathDto, style: MapboxStyle[]): void {
        this.currentEdgeStyle = [];
        this.currentNodeStyle = [];
        this.currentAllRoutesStyle = style;

        const featureCollection: FeatureCollection<Geometry, GeoJsonProperties> = this.toFeatureCollection([
            ...networkPath.edges,
            ...networkPath.nodes,
        ]);

        this.addGeoJSONSource(NETWORK_PATH_ALL_ROUTES_SOURCE_ID, featureCollection);
        this.addSourceStyle(NETWORK_PATH_ALL_ROUTES_SOURCE_ID, style);
    }

    private displayRoute(networkPath: NetworkPathDto, edgeStyle: MapboxStyle[], nodeStyle: MapboxStyle[]): void {
        this.currentEdgeStyle = edgeStyle;
        this.currentNodeStyle = nodeStyle;
        this.currentAllRoutesStyle = [];

        const edgeFeatureCollection: FeatureCollection<Geometry, GeoJsonProperties> = this.toFeatureCollection(
            networkPath.edges,
        );
        const nodeFeatureCollection: FeatureCollection<Geometry, GeoJsonProperties> = this.toFeatureCollection(
            networkPath.nodes,
        );

        this.addGeoJSONSource(NETWORK_PATH_EDGE_SOURCE_ID, edgeFeatureCollection);
        this.addSourceStyle(NETWORK_PATH_EDGE_SOURCE_ID, edgeStyle);

        this.addGeoJSONSource(NETWORK_PATH_NODE_SOURCE_ID, nodeFeatureCollection);
        this.addSourceStyle(NETWORK_PATH_NODE_SOURCE_ID, nodeStyle);
    }

    private async getLayerStyle(layerStyleId: string): Promise<MapboxStyle[]> {
        const layerStyle: LayerStyle = await firstValueFrom(
            this.layerStylesService.layerStyleControllerGetOne({
                id: layerStyleId,
            }),
        );

        return this.getNetworkPathStyle(layerStyle.style);
    }

    private getNetworkPathStyle(styles: MapboxStyle[]): MapboxStyle[] {
        return styles.map((style: MapboxStyle): MapboxStyle => {
            const styleWithoutZoom: MapboxStyle = { ...style };

            delete styleWithoutZoom.minzoom;
            delete styleWithoutZoom.maxzoom;

            return styleWithoutZoom;
        });
    }

    private async loadAndDisplayAllRoutes(
        networkPath: NetworkPathDto,
        layerStyleId: string,
        requestId: number,
    ): Promise<void> {
        const allRoutesStyle: MapboxStyle[] = await this.getLayerStyle(layerStyleId);

        if (requestId !== this.displayRequestId) {
            return;
        }

        this.resetSources();
        this.displayAllRoutes(networkPath, allRoutesStyle);
        this.centerMapOnNetworkPath(networkPath);
    }

    private async loadAndDisplayRoute(
        networkPath: NetworkPathDto,
        edgeLayerStyleId: string,
        nodeLayerStyleId: string,
        requestId: number,
    ): Promise<void> {
        const [edgeStyle, nodeStyle]: [MapboxStyle[], MapboxStyle[]] = await Promise.all([
            this.getLayerStyle(edgeLayerStyleId),
            this.getLayerStyle(nodeLayerStyleId),
        ]);

        if (requestId !== this.displayRequestId) {
            return;
        }

        this.resetSources();
        this.displayRoute(networkPath, edgeStyle, nodeStyle);
        this.centerMapOnNetworkPath(networkPath);
    }

    private removeSource(sourceId: string): void {
        if (!this.map) {
            return;
        }

        const mapStyle: StyleSpecification = this.map.getStyle();

        mapStyle.layers
            .filter((layer: LayerSpecification): boolean => layer.type !== 'background' && layer.source === sourceId)
            .forEach((layer: LayerSpecification): void => {
                this.map!.removeLayer(layer.id);
            });

        if (this.map.getSource(sourceId)) {
            this.map.removeSource(sourceId);
        }
    }

    private resetSources(): void {
        this.removeSource(NETWORK_PATH_NODE_SOURCE_ID);
        this.removeSource(NETWORK_PATH_EDGE_SOURCE_ID);
        this.removeSource(NETWORK_PATH_ALL_ROUTES_SOURCE_ID);
    }

    private toFeature(item: NetworkPathMapItem): Feature {
        const { geom, ...properties } = item;

        AssertService.assertGeoJsonGeometryType(geom);

        return {
            geometry: geom,
            properties,
            type: 'Feature',
        };
    }

    private toFeatureCollection(items: NetworkPathMapItem[]): FeatureCollection {
        return {
            features: items.map((item: NetworkPathMapItem) => this.toFeature(item)),
            type: 'FeatureCollection',
        };
    }
}
